import { useEffect, useRef } from 'react';
import { Blocker, BlockerFunction, useBlocker } from 'react-router-dom';
import { useModal } from '../common/modal';
import { Modals } from '../constants/modals';
import { useStore } from '../stores/store';
import { removeLanguagePrefix } from '../utils/uri/uri.utils';
import { SearchParam } from '../constants/route';

export type BlockedHistoryNavigation = Extract<Blocker, { state: 'blocked' }>;

interface UseNavigationBlockerOptions {
  Route: string;
  isInProgress: Boolean;
  setIsInProgress: (value: boolean) => void;
  service?: string;
  onLeave?: () => void;
  onRemain?: () => void;
  onHistoryNavigation?: (blocker: BlockedHistoryNavigation) => void;
}

export const useNavigationBlocker = ({
  Route,
  isInProgress,
  setIsInProgress,
  service = undefined,
  onLeave,
  onRemain,
  onHistoryNavigation,
}: UseNavigationBlockerOptions) => {
  const {
    userStore: { logout, isLogoutRequested, setIsLogoutRequested },
    navStore: { setSelectedService },
  } = useStore();

  const { openModal, closeModal } = useModal();
  const isHistoryNavigation = useRef(false);

  const shouldBlockPredicate: BlockerFunction = ({
    nextLocation,
    currentLocation,
    historyAction,
  }) => {
    const currentPath = removeLanguagePrefix(currentLocation.pathname);
    const nextPath = removeLanguagePrefix(nextLocation.pathname);

    if (
      currentPath === nextPath &&
      currentLocation.search === nextLocation.search
    ) {
      return false;
    }

    const navigateToSearchParam = new URLSearchParams(nextLocation.search).get(
      SearchParam.NAVIGATE_TO
    );

    const shouldBlock =
      isInProgress &&
      (navigateToSearchParam !== null
        ? !navigateToSearchParam.includes(Route)
        : !nextPath.includes(Route));

    isHistoryNavigation.current = !!(
      onHistoryNavigation &&
      isInProgress &&
      !shouldBlock &&
      historyAction === 'POP' &&
      currentPath.includes(Route) &&
      nextPath.includes(Route)
    );

    return shouldBlock || isHistoryNavigation.current;
  };

  const blocker = useBlocker(shouldBlockPredicate);

  const handleStay = () => {
    closeModal(Modals.LEAVE_THIS_PAGE);

    blocker.reset?.();

    if (isLogoutRequested) {
      setIsLogoutRequested(false);
    }

    if (setSelectedService) {
      setSelectedService(service);
    }

    if (onRemain) {
      onRemain();
    }
  };

  const handleLeave = () => {
    setIsInProgress(false);

    if (isLogoutRequested) {
      logout();
    }

    blocker.proceed?.();
    onLeave && onLeave();
  };

  useEffect(() => {
    if (blocker.state === 'blocked') {
      if (isHistoryNavigation.current && onHistoryNavigation) {
        onHistoryNavigation(blocker);
        return;
      }
      openModal({
        id: Modals.LEAVE_THIS_PAGE,
        name: Modals.LEAVE_THIS_PAGE,
        props: {
          onApprove: handleStay,
          onClose: handleLeave,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocker]);

  return blocker;
};
